import React from "react";
import { Col, Row } from "react-bootstrap";
import { useIntl } from "react-intl";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { UserData } from "checkout/ts/redux/models/user-data";
import { isTransactionThreeDsAddressRequired } from "checkout/ts/redux/utils/token";
import { isDarkTheme } from "checkout/ts/utils/colors";
import { UnivaMetadata } from "checkout/ts/utils/metadata";
import { getStepUrl, StepName } from "checkout/ts/utils/StepName";
import { STATES_BY_COUNTRY } from "common/locale/states";
import { isEmpty } from "lodash";
import { compose } from "recompose";
import { Form, formValueSelector, InjectedFormProps, reduxForm } from "redux-form";
import { PaymentType, TransactionTokenCardDataItem } from "univapay-node";

import { FORM_ADDRESS, FORM_TOKEN_SELECT_NAME } from "../../../../../../common/constants";
import { Countries } from "../../../../../../common/locale/constants";
import { LOCALE_LABELS } from "../../../../locale/labels";
import { Dispatch, StateShape } from "../../../../redux/store";
import { validationToErrors } from "../../../../utils/errors";
import { StepTitle } from "../../../common/StepTitle";
import { DialogButton } from "../../../layout/DialogButton";
import { PrivacyLink } from "../FormData";
import { getRequiredFields } from "../utils/customerInfo";

import { BillingAddressEn } from "./BillingAddressEn";
import { BillingAddressJa } from "./BillingAddressJa";

type OwnProps = {
    title?: string;
    onSubmit?: (values: Partial<UserData>) => void | Promise<void>;
    nextButtonLabel: React.ReactNode;
    showPrivacyLink?: boolean;
};

type ComposedProps = ReturnType<typeof stateSelector> & InjectedFormProps<Partial<UserData>> & OwnProps;

const stateSelector = (state: StateShape) => {
    const { checkout, application, configuration, tokens } = state;

    const { invalidFields } = application;

    const {
        shippingAddressLine1,
        shippingAddressLine2,
        shippingAddressCity,
        shippingAddressState,
        shippingAddressZip,
        shippingAddressCountryCode,
        submitButtonText,
    } = application.params.params;

    const selectedTokenId = formValueSelector(FORM_TOKEN_SELECT_NAME)(state, "selectedTokenId");
    const customerIdToken = tokens?.[selectedTokenId];

    const { requireBillingAddress, forceThreeDsAddress } = getRequiredFields(
        checkout.paymentType,
        state.application,
        configuration?.data,
        customerIdToken
    );

    const countryCode = checkout.paymentType === PaymentType.PAIDY ? "JP" : shippingAddressCountryCode?.toUpperCase();

    const requireThreeDsAddress = isTransactionThreeDsAddressRequired(application.params.params, configuration.data);

    const country = Countries[countryCode]
        ? countryCode
        : Countries[(customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.country]
        ? (customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.country
        : Countries[customerIdToken?.metadata?.[UnivaMetadata.ADDRESS_COUNTRY]]
        ? customerIdToken?.metadata?.[UnivaMetadata.ADDRESS_COUNTRY]
        : requireThreeDsAddress || requireBillingAddress
        ? "JP"
        : undefined;

    const availableStates = country ? STATES_BY_COUNTRY[country.toLocaleUpperCase()] : undefined;

    const queryState =
        shippingAddressState ||
        (customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.state ||
        customerIdToken?.metadata?.[UnivaMetadata.ADDRESS_STATE];

    const addressState =
        availableStates && queryState && requireThreeDsAddress
            ? availableStates.find((state) => state.name.toLowerCase() === queryState.toLowerCase())?.name
            : queryState;

    return {
        paymentType: checkout.paymentType,
        submitButtonText,
        requireBillingAddress: requireBillingAddress || forceThreeDsAddress || (requireThreeDsAddress && addressState),
        processing: ((state.loading.models.userData || state.loading.models.checkout) as unknown) as boolean,
        initialValues: {
            data: {
                line1:
                    shippingAddressLine1 ||
                    (customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.line1 ||
                    customerIdToken?.metadata?.[UnivaMetadata.ADDRESS_LINE1],
                line2:
                    shippingAddressLine2 ||
                    (customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.line2 ||
                    customerIdToken?.metadata?.[UnivaMetadata.ADDRESS_LINE2],
                city:
                    shippingAddressCity ||
                    (customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.city ||
                    customerIdToken?.metadata?.[UnivaMetadata.ADDRESS_CITY],
                state: addressState,
                zip:
                    shippingAddressZip ||
                    (customerIdToken?.data as TransactionTokenCardDataItem)?.billing?.zip ||
                    customerIdToken?.metadata?.[UnivaMetadata.ADDRESS_ZIP],
                country,
            },
        },
        darkTheme: isDarkTheme(state),
        paymentMethodKey: state.checkout.paymentMethodKey,
        invalidFields,
    };
};

export const validate = (values: Partial<UserData>, { requireBillingAddress }: ComposedProps) => {
    const { data: { line1, city, state, zip, country } = {} } = values;

    return validationToErrors({
        "data.line1": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: requireBillingAddress && isEmpty(line1),
            [LOCALE_LABELS.VALIDATION_INVALID_ADDRESS_LENGTH]: line1 && line1.length > 50,
        },
        "data.city": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: requireBillingAddress && isEmpty(city),
            [LOCALE_LABELS.VALIDATION_INVALID_ADDRESS_LENGTH]: city && city.length > 50,
        },
        "data.state": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: requireBillingAddress && isEmpty(state),
        },
        "data.zip": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: requireBillingAddress && isEmpty(zip),
            [LOCALE_LABELS.VALIDATION_ZIP]: zip && !/^[-0-9]{7}/.test(zip),
            [LOCALE_LABELS.VALIDATION_INVALID_ZIP_LENGTH]: zip && zip.length > 16,
        },
        "data.country": {
            [LOCALE_LABELS.VALIDATION_REQUIRED]: requireBillingAddress && !country,
        },
    });
};

export const Content = compose<ComposedProps, OwnProps>(
    connect(stateSelector),
    reduxForm({ destroyOnUnmount: false, forceUnregisterOnUnmount: true, form: FORM_ADDRESS, validate })
)(
    ({
        paymentType,
        processing,
        requireBillingAddress,
        submitButtonText,
        handleSubmit,
        title,
        onSubmit,
        darkTheme,
        nextButtonLabel,
        showPrivacyLink,
        paymentMethodKey,
        invalidFields,
    }) => {
        const { push: redirect } = useHistory();
        const { locale, formatMessage } = useIntl();

        const {
            userData: { setUserData },
        } = useDispatch<Dispatch>();

        const isPaidy = paymentType === PaymentType.PAIDY;

        const handleFormSubmit = async (values: Partial<UserData>) => {
            await setUserData(values);

            const submitCallback =
                onSubmit ||
                (() =>
                    redirect(getStepUrl(paymentType, paymentMethodKey, isPaidy ? StepName.PROCESSING : StepName.DATA)));
            await submitCallback(values);
        };

        const AddressComponent = (() => {
            switch (locale.substring(0, 2).toLowerCase()) {
                case "ja":
                    return BillingAddressJa;

                default:
                    return BillingAddressEn;
            }
        })();

        return (
            <Form
                data-name="form-address"
                noValidate
                onSubmit={handleSubmit(handleFormSubmit)}
                className="address content-form">
                <Row>
                    <Col xs={12}>
                        <StepTitle>{title || formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_TITLE })}</StepTitle>
                    </Col>

                    <Col xs={12}>
                        {requireBillingAddress && (
                            <AddressComponent
                                staticCountry={paymentType === PaymentType.PAIDY ? "JP" : undefined}
                                isDark={darkTheme}
                                invalidFields={invalidFields}
                            />
                        )}
                    </Col>
                </Row>

                {showPrivacyLink && <PrivacyLink isDark={darkTheme} />}

                <DialogButton disabled={processing} tabIndex={10}>
                    {nextButtonLabel || submitButtonText || isPaidy
                        ? formatMessage({ id: LOCALE_LABELS.COMMON_BUTTONS_SUBMIT })
                        : formatMessage({ id: LOCALE_LABELS.COMMON_BUTTONS_NEXT })}
                </DialogButton>
            </Form>
        );
    }
);

export default Content;
